import { Link } from 'react-router-dom';
import { 
  Phone, 
  Heart, 
  Shield, 
  Clock, 
  Users, 
  ArrowRight, 
  CheckCircle2,
  HeartHandshake,
  Home as HomeIcon,
  Stethoscope,
  Star,
  Quote,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { TrustBadges } from '../components/TrustBadges';

export default function Home() {
  const services = [
    {
      icon: HeartHandshake,
      title: 'Grundpflege',
      description: 'Liebevolle Unterstützung bei der täglichen Körperpflege, Ankleiden und Mobilität.',
      color: 'from-[#1A52D8]/10 to-[#23D3D3]/10'
    },
    {
      icon: Stethoscope,
      title: 'Behandlungspflege',
      description: 'Medizinische Versorgung wie Medikamentengabe, Wundversorgung und Injektionen.',
      color: 'from-[#23D3D3]/10 to-[#98E72B]/10'
    },
    {
      icon: HomeIcon,
      title: 'Hauswirtschaft',
      description: 'Hilfe im Haushalt, Einkäufe, Mahlzeitenzubereitung und mehr.',
      color: 'from-[#98E72B]/10 to-[#1A52D8]/10'
    },
  ];

  const whyUs = [
    { icon: Heart, title: 'Menschlichkeit', description: 'Wir behandeln jeden Menschen mit Würde und echter Zuwendung.' },
    { icon: Shield, title: 'Verlässlichkeit', description: 'Pünktlich, verbindlich und immer erreichbar.' },
    { icon: Users, title: 'Erfahrung', description: 'Qualifizierte Pflegefachkräfte mit Berufserfahrung.' },
    { icon: Clock, title: 'Flexibilität', description: 'Individuelle Pflegepläne für Ihre Bedürfnisse.' },
  ];

  const testimonials = [
    {
      quote: 'Die Pflegerinnen sind wie Familie geworden. Immer freundlich, immer zuverlässig. Meine Mutter fühlt sich rundum wohl.',
      author: 'Sabine M.',
      location: 'Itzehoe',
      rating: 5
    },
    {
      quote: 'Endlich kann ich beruhigt arbeiten, weil ich weiß, dass mein Vater in den besten Händen ist. Vielen Dank!',
      author: 'Michael K.',
      location: 'Glückstadt',
      rating: 5
    },
    {
      quote: 'Die individuelle Betreuung und das Verständnis für meine Situation haben mir sehr geholfen. Absolut empfehlenswert.',
      author: 'Ingrid B.',
      location: 'Krempe',
      rating: 5
    },
  ];

  const faqs = [
    {
      question: 'Wie schnell kann die Pflege beginnen?',
      answer: 'Nach einem ersten Beratungsgespräch können wir in der Regel innerhalb von 24-48 Stunden mit der Pflege beginnen. In dringenden Fällen auch am selben Tag.',
    },
    {
      question: 'Werden die Kosten von der Pflegekasse übernommen?',
      answer: 'Ja, viele unserer Leistungen werden von der Pflegekasse übernommen. Je nach Pflegegrad stehen Ihnen Sachleistungen von 761€ bis 2.095€ monatlich zu. Wir beraten Sie kostenlos.',
    },
    {
      question: 'Welche Leistungen bieten Sie an?',
      answer: 'Wir bieten Grundpflege, Behandlungspflege, hauswirtschaftliche Versorgung, Pflegeberatung, Unterstützung nach Krankenhausaufenthalt und Entlastungsleistungen an.',
    },
    {
      question: 'Sind Sie auch am Wochenende erreichbar?',
      answer: 'Ja, wir sind rund um die Uhr erreichbar – 24 Stunden am Tag, 7 Tage die Woche, 365 Tage im Jahr.',
    },
    {
      question: 'Wie läuft das Erstgespräch ab?',
      answer: 'Im kostenlosen Erstgespräch lernen wir Sie kennen, besprechen Ihre Wünsche und erstellen gemeinsam einen individuellen Pflegeplan.',
    },
  ];

  return (
    <main id="main-content" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" aria-labelledby="hero-heading">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#23D3D3]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#1A52D8]/5 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10 py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A52D8]/5 rounded-full mb-6">
                <Clock className="w-4 h-4 text-[#1A52D8]" />
                <span className="text-sm font-medium text-[#1A52D8]">24/7 für Sie erreichbar</span>
              </div>
              
              <h1 id="hero-heading" className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] leading-[1.1] mb-6 hero-text">
                Ambulante Pflege in 
                <span className="text-[#1A52D8]"> Itzehoe</span> mit Herz
              </h1>
              
              <p className="text-lg md:text-xl text-[#64748b] leading-relaxed mb-8 max-w-xl service-description">
                Professionelle häusliche Pflege für Senioren. Grundpflege, Behandlungspflege, 
                Hauswirtschaft – persönlich und zuverlässig.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <a href="tel:+4915146173685" data-testid="hero-phone-btn">
                  <Button className="bg-[#1A52D8] text-white hover:bg-[#1545b8] h-14 px-8 rounded-full text-lg font-semibold shadow-lg shadow-[#1A52D8]/25 hover:shadow-xl transition-all duration-300 w-full sm:w-auto">
                    <Phone className="w-5 h-5" />
                    Jetzt Anrufen
                  </Button>
                </a>
                <Link to="/kontakt" data-testid="hero-callback-btn">
                  <Button className="bg-white text-[#1A52D8] border-2 border-[#e2e8f0] hover:border-[#1A52D8] h-14 px-8 rounded-full text-lg font-semibold transition-all duration-300 w-full sm:w-auto">
                    Rückruf anfordern
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2 text-[#64748b]">
                  <div className="w-5 h-5 rounded-full bg-[#23D3D3]/20 flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-[#23D3D3]" />
                  </div>
                  <span className="text-sm">Kostenlose Beratung</span>
                </div>
                <div className="flex items-center gap-2 text-[#64748b]">
                  <div className="w-5 h-5 rounded-full bg-[#23D3D3]/20 flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-[#23D3D3]" />
                  </div>
                  <span className="text-sm">Schneller Start</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative">
                {/* Main Image */}
                <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-[#1A52D8]/10">
                  <img
                    src="https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                    alt="Pflegekraft betreut liebevoll älteres Ehepaar zuhause"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="eager"
                  />
                </div>
                
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-[#e2e8f0] hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-[#1e293b]">Kreis Steinburg</p>
                      <p className="text-sm text-[#64748b]">Itzehoe & Umgebung</p>
                    </div>
                  </div>
                </div>

                {/* Stats Card */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl px-5 py-3 shadow-xl border border-[#e2e8f0] hidden lg:block">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#fbbf24] text-[#fbbf24]" />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-[#1e293b]">4.9</span>
                    <span className="text-xs text-[#64748b]">(47 Bewertungen)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <TrustBadges />

      {/* Services Section */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#1A52D8]/5 rounded-full text-[#1A52D8] font-medium text-sm mb-4">
              Unsere Leistungen
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e293b] mb-4">
              Professionelle Pflege für Sie
            </h2>
            <p className="text-lg text-[#64748b] max-w-2xl mx-auto">
              Von der Grundpflege bis zur medizinischen Versorgung – individuell auf Sie abgestimmt.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                className="group relative bg-white rounded-2xl p-8 border border-[#e2e8f0] hover:border-[#1A52D8]/30 hover:shadow-xl hover:shadow-[#1A52D8]/5 transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1e293b] mb-3">{service.title}</h3>
                  <p className="text-[#64748b] leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/leistungen" data-testid="all-services-link">
              <Button className="bg-white text-[#1A52D8] border-2 border-[#1A52D8]/20 hover:border-[#1A52D8] hover:bg-[#1A52D8]/5 h-12 px-8 rounded-full font-semibold transition-all duration-300">
                Alle Leistungen ansehen
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 bg-gradient-to-b from-[#f8fafb] to-white" data-testid="why-us-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1765896387377-e293914d1e69?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Vertrauensvolle Begegnung zwischen Pflegekraft und Seniorin"
                  className="w-full h-auto object-cover aspect-square"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              {/* Accent Shape */}
              <div className="absolute -z-10 -bottom-8 -right-8 w-full h-full bg-gradient-to-br from-[#1A52D8]/10 to-[#23D3D3]/10 rounded-[2rem]" />
            </div>

            {/* Content Side */}
            <div>
              <span className="inline-block px-4 py-1.5 bg-[#23D3D3]/10 rounded-full text-[#23D3D3] font-medium text-sm mb-4">
                Warum wir?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e293b] mb-6">
                Pflege auf Augenhöhe
              </h2>
              <p className="text-lg text-[#64748b] leading-relaxed mb-10">
                Unser Team aus qualifizierten Pflegefachkräften betreut Sie persönlich 
                und individuell in Ihrer gewohnten Umgebung.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {whyUs.map((item, index) => (
                  <div key={item.title} className="flex gap-4" data-testid={`why-us-${index}`}>
                    <div className="w-12 h-12 rounded-xl bg-[#1A52D8]/5 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#1A52D8]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1e293b] mb-1">{item.title}</h3>
                      <p className="text-sm text-[#64748b] leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#fbbf24]/10 rounded-full text-[#b45309] font-medium text-sm mb-4">
              Kundenstimmen
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e293b] mb-4">
              Das sagen unsere Kunden
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#f8fafb] to-white rounded-2xl p-8 border border-[#e2e8f0] relative"
                data-testid={`testimonial-${index}`}
              >
                <Quote className="absolute top-6 right-6 w-10 h-10 text-[#1A52D8]/10" />
                
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#fbbf24] text-[#fbbf24]" />
                  ))}
                </div>
                
                <p className="text-[#475569] leading-relaxed mb-6">
                  &bdquo;{testimonial.quote}&ldquo;
                </p>
                
                <div className="flex items-center gap-3 pt-4 border-t border-[#e2e8f0]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] flex items-center justify-center text-white font-bold text-sm">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-[#1e293b]">{testimonial.author}</p>
                    <p className="text-sm text-[#64748b]">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#f8fafb]" data-testid="faq-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-[#1A52D8]/5 rounded-full text-[#1A52D8] font-medium text-sm mb-4">
                Häufige Fragen
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e293b] mb-4">
                Ihre Fragen, unsere Antworten
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="bg-white rounded-xl px-6 border border-[#e2e8f0] shadow-sm data-[state=open]:shadow-md transition-shadow"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-[#1e293b] hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#64748b] leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A52D8] via-[#1A52D8] to-[#23D3D3]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="container-custom text-center relative z-10">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Wir sind für Sie da
            </h2>
            <p className="text-xl text-white/80 mb-10 leading-relaxed">
              Kostenlose und unverbindliche Beratung – rufen Sie uns an oder fordern Sie einen Rückruf an.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+4915146173685" data-testid="cta-phone-btn">
                <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300">
                  <Phone className="w-5 h-5" />
                  Jetzt Anrufen
                </Button>
              </a>
              <Link to="/kontakt" data-testid="cta-contact-btn">
                <Button className="bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 h-14 px-10 rounded-full text-lg font-semibold transition-all duration-300">
                  Rückruf anfordern
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Grid - ALLE 18 STÄDTE */}
      <section className="py-20 bg-gradient-to-b from-white to-[#f8fafb]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#23D3D3]/10 rounded-full text-[#23D3D3] font-medium text-sm mb-4">
              Unser Einzugsgebiet
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e293b] mb-4">
              Wir betreuen 18 Orte im Kreis Steinburg
            </h2>
            <p className="text-lg text-[#64748b] max-w-2xl mx-auto">
              Von unserem Standort in Dägeling erreichen wir alle Gemeinden schnell und zuverlässig - 
              meist in unter 25 Minuten.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { name: 'Itzehoe', slug: 'itzehoe', distance: '6 km', tag: 'Kreisstadt' },
              { name: 'Glückstadt', slug: 'glueckstadt', distance: '12 km', tag: 'Hafenstadt' },
              { name: 'Wilster', slug: 'wilster', distance: '18 km', tag: 'Historisch' },
              { name: 'Kellinghusen', slug: 'kellinghusen', distance: '15 km', tag: 'An der Stör' },
              { name: 'Krempe', slug: 'krempe', distance: '14 km', tag: 'Altstadt' },
              { name: 'Hohenlockstedt', slug: 'hohenlockstedt', distance: '16 km', tag: 'Geest' },
              { name: 'Dägeling', slug: 'daegeling', distance: '0 km', tag: 'Unser Sitz' },
              { name: 'Heiligenstedten', slug: 'heiligenstedten', distance: '8 km', tag: 'Sehr nah' },
              { name: 'Lägerdorf', slug: 'laegerdorf', distance: '10 km', tag: 'Zementwerk' },
              { name: 'Breitenburg', slug: 'breitenburg', distance: '7 km', tag: 'Schloss' },
              { name: 'Wewelsfleth', slug: 'wewelsfleth', distance: '16 km', tag: 'Störhafen' },
              { name: 'Horst', slug: 'horst-holstein', distance: '11 km', tag: '6.500 EW' },
              { name: 'Schenefeld', slug: 'schenefeld-steinburg', distance: '9 km', tag: 'Nah' },
              { name: 'Münsterdorf', slug: 'muensterdorf', distance: '13 km', tag: 'Ländlich' },
              { name: 'Brokdorf', slug: 'brokdorf', distance: '19 km', tag: 'Elbe' },
              { name: 'Kollmar', slug: 'kollmar', distance: '20 km', tag: 'Elbdeich' },
              { name: 'Ecklak', slug: 'ecklak', distance: '12 km', tag: 'Klein' },
              { name: 'Kaisborstel', slug: 'kaisborstel', distance: '14 km', tag: 'Ruhig' }
            ].map((city) => (
              <Link 
                key={city.slug}
                to={`/ambulante-pflege/${city.slug}`}
                className="group bg-white hover:bg-gradient-to-br hover:from-[#f8fafb] hover:to-white rounded-xl p-5 border-2 border-[#e2e8f0] hover:border-[#1A52D8] transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <MapPin className="w-5 h-5 text-[#23D3D3] group-hover:text-[#1A52D8] transition-colors flex-shrink-0" />
                  <span className="text-xs px-2 py-1 bg-[#23D3D3]/10 text-[#23D3D3] rounded-full">{city.tag}</span>
                </div>
                <h3 className="font-bold text-[#1e293b] group-hover:text-[#1A52D8] transition-colors mb-1">
                  {city.name}
                </h3>
                <div className="text-xs text-[#94a3b8]">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {city.distance === '0 km' ? 'Unser Standort' : `${city.distance} von Dägeling`}
                </div>
              </Link>
            ))}
          </div>

          <p className="text-center text-[#64748b] mt-8">
            Auch in weiteren Gemeinden im Kreis Steinburg sind wir für Sie da.
          </p>
        </div>
      </section>

      {/* SEO Text Content */}
      <section className="py-12 bg-[#f8fafb] border-t border-[#e2e8f0]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-6">
              Ambulante Pflege im Kreis Steinburg
            </h2>
            <div className="prose prose-lg text-[#64748b] leading-relaxed space-y-4">
              <p>
                Das <strong>Phönix Pflegeteam Itzehoe</strong> ist Ihr zuverlässiger Partner für <strong>ambulante Pflege in Itzehoe</strong> und dem gesamten Kreis Steinburg. 
                Wir betreuen Senioren und Pflegebedürftige in ihrer vertrauten häuslichen Umgebung.
              </p>
              <p>
                Unser Einzugsgebiet umfasst <strong>Itzehoe, Glückstadt, Wilster, Kellinghusen, Krempe, Hohenlockstedt, Dägeling, Heiligenstedten, Lägerdorf, Breitenburg, Wewelsfleth, Horst, Schenefeld, Münsterdorf, Brokdorf, Kollmar</strong> und weitere Gemeinden. 
                Die Kosten werden größtenteils von der Pflegekasse übernommen – wir beraten Sie kostenlos.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
